/**
 * Geocodificador especializado CDAV Hidrografía (G03)
 *
 * Conecta con servicio WFS CDAV_02_hidrografia (sustituto oficial de
 * DERA_g3_hidrografia desde migración IECA 2026-03-18) para infraestructuras
 * hidráulicas:
 * - cav_02_g03_04_Manantial: Manantiales (~2.500 en Andalucía)
 * - cav_02_g03_09_Deposito:  Depósitos de agua (~1.800 en Andalucía)
 * - cav_02_g03_07_Captacion: ELIMINADA en CDAV — no disponible (ver nota)
 *
 * CARACTERÍSTICAS:
 * - CRS nativo: EPSG:25830 (sin transformación necesaria)
 * - Filtrado por cod_mun (código INE 5 dígitos)
 * - Precisión: ±5-15 m (coordenadas oficiales IECA)
 *
 * Endpoint WFS (RESIL-WFS-S1 — actualizado 2026-03-19):
 * https://www.ideandalucia.es/services/CDAV_02_hidrografia/wfs
 *
 * NOTA: La capa g03_07_Captacion no existe en CDAV_02_hidrografia.
 * El enum DERAHydraulicLayerType.CAPTACION se conserva por compatibilidad
 * de tipos pero se omite en getByMunicipality() para evitar errores 400.
 *
 * Test validación: Colomera (18051) → 3 depósitos, 9 manantiales
 *
 * @module services/geocoding/specialized
 * @version 2.0.0 — RESIL-WFS-S1: migración DERA_g3 → CDAV_02_hidrografia
 */

import type { WFSFeature, WFSSearchOptions } from './WFSBaseGeocoder';
import { WFSBaseGeocoder } from './WFSBaseGeocoder';
import type { SpecializedGeocoderConfig, GeocodingResult } from '../../../types/infrastructure';

/**
 * Tipos de capas hidráulicas disponibles en DERA G03
 *
 * Catalogación verificada empíricamente (sub-DERA-HENUM 12-may-2026,
 * ver `.ptel/DECISION_DERA_G03_CATALOGACION_20260512.md`):
 * - MANANTIAL, DEPOSITO: puntuales clásicos.
 * - CAPTACION: eliminada en CDAV (string vacío, conservada por compat. tipos).
 * - EMBALSE: sub-WEMB, MultiPolygon, sin `cod_mun` directo.
 * - PRESA: sub-DERA-HENUM, MultiPoint(N=1), n=592, sin `cod_mun` directo,
 *   complementaria a EMBALSE (dique puntual de cierre).
 */
export enum DERAHydraulicLayerType {
  /** Manantiales - fuentes naturales de agua */
  MANANTIAL = 'MANANTIAL',
  /** Depósitos de agua - almacenamiento */
  DEPOSITO = 'DEPOSITO',
  /** Captaciones - pozos y sondeos */
  CAPTACION = 'CAPTACION',
  /** Embalses - sub-WEMB · cav_02_g03_06_Embalse, n=611 Andalucía (100 % MultiPolygon) */
  EMBALSE = 'EMBALSE',
  /** Presas - sub-DERA-HENUM · cav_02_g03_12_Presa, n=592 Andalucía (100 % MultiPoint(N=1)) · complementaria a EMBALSE */
  PRESA = 'PRESA',
}

/**
 * Mapeo de tipos a nombres de capas WFS en CDAV_02_hidrografia.
 * RESIL-WFS-S1: prefijo cav_02_ obligatorio.
 * CAPTACION no existe en CDAV — valor vacío para omitirla en getByMunicipality().
 */
export const DERA_HYDRAULIC_LAYERS: Record<DERAHydraulicLayerType, string> = {
  [DERAHydraulicLayerType.MANANTIAL]: 'cav_02_g03_04_Manantial',
  [DERAHydraulicLayerType.DEPOSITO]:  'cav_02_g03_09_Deposito',
  [DERAHydraulicLayerType.CAPTACION]: '', // eliminada en CDAV_02_hidrografia
  [DERAHydraulicLayerType.EMBALSE]:   'cav_02_g03_06_Embalse', // sub-WEMB
  [DERAHydraulicLayerType.PRESA]:     'cav_02_g03_12_Presa',   // sub-DERA-HENUM
};

/**
 * Campos clave por capa DERA Hidrografía
 * Verificados via GetCapabilities/DescribeFeatureType
 */
export interface DERAHydraulicFields {
  /** Campo para nombre de la infraestructura */
  nameField: string;
  /** Campos adicionales específicos */
  additionalFields: string[];
}

/**
 * Esquemas de campos por capa CDAV_02_hidrografia.
 * Los nombres de campo internos (nombre, cod_mun, c_m3…) se conservan
 * sin cambios respecto a DERA_g3.
 */
export const DERA_LAYER_FIELDS: Record<string, DERAHydraulicFields> = {
  'cav_02_g03_04_Manantial': {
    nameField: 'nombre',
    additionalFields: ['tipologia', 'enlace_web', 'enlace_fot', 'cod_mun'],
  },
  'cav_02_g03_09_Deposito': {
    nameField: 'nombre',
    additionalFields: ['c_m3', 'cota', 'estado', 'red', 'cod_mun'],
  },
  // sub-WEMB: schema empírico verificado 12-may sobre n=611 features.
  // Las features carecen de cod_mun directo; el filtro municipal se hace
  // a posteriori vía intersección espacial con el polígono municipal
  // (queda fuera de scope WEMB; el geocoder cae a la rama auto-layer
  // genérica que busca por nombre sin filtro INE).
  'cav_02_g03_06_Embalse': {
    nameField: 'nombre',
    additionalFields: ['codigo', 'cuenca', 'tipo', 'titular', 'estado', 'id_dera'],
  },
  // sub-DERA-HENUM: schema empírico verificado 12-may sobre n=5 features sample
  // (resmoke al inicio de la sesión confirmó 100 % MultiPoint(N=1) + rango UTM30
  // Andalucía + schema {codigo, estado, id_dera, nombre, tipo}). Las features
  // carecen de cod_mun directo, igual que Embalse — el filtro municipal queda
  // fuera de scope (geometría puntual pero sin codificación INE en el schema).
  'cav_02_g03_12_Presa': {
    nameField: 'nombre',
    additionalFields: ['codigo', 'tipo', 'estado', 'id_dera'],
  },
};

/**
 * Opciones de búsqueda específicas para DERA Hidrografía
 */
export interface DERAHydraulicSearchOptions extends WFSSearchOptions {
  /** Código INE del municipio (5 dígitos) - FILTRADO PRINCIPAL */
  codMun?: string;
  /** Tipo de capa a consultar */
  layerType?: DERAHydraulicLayerType;
}

/**
 * Feature DERA Hidrografía con campos específicos
 */
export interface DERAHydraulicFeature extends WFSFeature {
  properties: {
    /** Código INE del municipio */
    codMun: string;
    /** Capacidad en m³ (solo depósitos) */
    capacidadM3?: number;
    /** Cota altimétrica (solo depósitos) */
    cota?: number;
    /** Estado operativo (depósitos y embalses) */
    estado?: string;
    /** Red de distribución (solo depósitos) */
    red?: string;
    /** Tipología (manantiales/captaciones) */
    tipologia?: string;
    /** Enlace web documentación */
    enlaceWeb?: string;
    /** Enlace foto */
    enlaceFoto?: string;
    // sub-WEMB: campos específicos cav_02_g03_06_Embalse
    /** Código identificador del embalse (sub-WEMB) */
    codigo?: string;
    /** Cuenca hidrográfica (sub-WEMB) */
    cuenca?: string;
    /** Tipo (uso) del embalse (sub-WEMB) */
    tipo?: string;
    /** Titular del embalse (sub-WEMB) */
    titular?: string;
    /** Identificador DERA del embalse (sub-WEMB) */
    idDera?: string;
    /** Capa origen */
    sourceLayer: string;
    [key: string]: unknown;
  };
}

/**
 * Geocodificador especializado DERA Hidrografía (G03)
 * 
 * Proporciona acceso a infraestructuras hidráulicas de IECA:
 * - Manantiales: fuentes naturales de agua
 * - Depósitos: almacenamiento de agua potable
 * - Captaciones: pozos y sondeos de extracción
 * 
 * @example
 * ```typescript
 * const geocoder = new DERAHydraulicGeocoder();
 * 
 * // Buscar por código municipal
 * const features = await geocoder.getByMunicipality('18051');
 * // Retorna ~12 infraestructuras en Colomera
 * 
 * // Geocodificar por nombre
 * const result = await geocoder.geocode({
 *   name: 'Manantial Cauro',
 *   codMun: '18051'
 * });
 * ```
 */
export class DERAHydraulicGeocoder extends WFSBaseGeocoder {
  
  /** Capa actualmente seleccionada */
  private currentLayer: string = DERA_HYDRAULIC_LAYERS[DERAHydraulicLayerType.DEPOSITO];

  /**
   * Configuración específica para WFS DERA G03 Hidrografía
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      // RESIL-WFS-S1: endpoint canónico CDAV_02_hidrografia (sustituye DERA_g3_hidrografia)
      wfsEndpoint: 'https://www.ideandalucia.es/services/CDAV_02_hidrografia/wfs',
      layerName: this.currentLayer,
      fuzzyThreshold: 0.35,
      timeout: 15000, // 15s
      outputSRS: 'EPSG:25830', // CRS nativo - sin transformación
    };
  }

  /**
   * Obtiene esquema de campos para la capa actual
   */
  private getLayerFields(): DERAHydraulicFields {
    return DERA_LAYER_FIELDS[this.currentLayer] || {
      nameField: 'nombre',
      additionalFields: ['cod_mun'],
    };
  }

  /**
   * Parsea feature GeoJSON de DERA Hidrografía
   * 
   * Maneja geometrías Point en EPSG:25830 (nativo)
   */
  protected parseFeature(feature: unknown): DERAHydraulicFeature | null {
    try {
      const f = feature as { 
        properties?: Record<string, unknown>; 
        geometry?: { type: string; coordinates: number[] };
        id?: string;
      };
      const props = f.properties || {};
      const geom = f.geometry;

      // Validar geometría vía parseGeometry (Point + MultiPoint N=1 — sub-0a)
      const pt = this.parseGeometry(geom, `DERAHydraulic feature ${String(f.id ?? 'sin-id')}`);
      if (!pt) {
        return null;
      }

      const [x, y] = pt;

      // Validar coordenadas UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        console.warn('DERAHydraulic: Coordenadas fuera de rango UTM30:', x, y);
        return null;
      }

      // Obtener nombre según campo de la capa
      const fields = this.getLayerFields();
      const name = String(props[fields.nameField] || '').trim();

      return {
        name,
        x,
        y,
        municipality: '', // No disponible directamente, usar cod_mun
        province: '',
        address: '',
        properties: {
          codMun: String(props['cod_mun'] || ''),
          capacidadM3: props['c_m3'] ? Number(props['c_m3']) : undefined,
          cota: props['cota'] ? Number(props['cota']) : undefined,
          estado: props['estado'] ? String(props['estado']) : undefined,
          red: props['red'] ? String(props['red']) : undefined,
          tipologia: props['tipologia'] ? String(props['tipologia']) : undefined,
          enlaceWeb: props['enlace_web'] ? String(props['enlace_web']) : undefined,
          enlaceFoto: props['enlace_fot'] ? String(props['enlace_fot']) : undefined,
          // sub-WEMB: campos específicos cav_02_g03_06_Embalse (g03_06)
          codigo: props['codigo'] ? String(props['codigo']) : undefined,
          cuenca: props['cuenca'] ? String(props['cuenca']) : undefined,
          tipo: props['tipo'] ? String(props['tipo']) : undefined,
          titular: props['titular'] ? String(props['titular']) : undefined,
          idDera: props['id_dera'] ? String(props['id_dera']) : undefined,
          sourceLayer: this.currentLayer,
        },
      };
    } catch (error) {
      console.error('DERAHydraulic: Error parseando feature:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL con cod_mun como filtro principal
   * 
   * DERA usa cod_mun (código INE 5 dígitos) para filtrado municipal,
   * diferente a otros WFS que usan MUNICIPIO como texto.
   *
   * Sub-WEMB: la capa `cav_02_g03_06_Embalse` no expone `cod_mun` (schema
   * empírico 12-may: id_dera/codigo/nombre/cuenca/tipo/titular/estado).
   * Aplicar `cod_mun='XXXXX'` devuelve HTTP 400 «Illegal property name».
   * El filtro municipal sobre Embalse queda fuera de scope WEMB (debe
   * hacerse a posteriori vía intersección espacial con polígono municipal).
   *
   * Sub-DERA-HENUM: idéntica situación para `cav_02_g03_12_Presa` (schema
   * empírico: id_dera/codigo/nombre/tipo/estado, sin cod_mun). El guard
   * cubre ambas capas mediante `isLayerWithoutCodMun`.
   */
  protected buildCQLFilter(options: DERAHydraulicSearchOptions): string {
    const filters: string[] = [];
    const fields = this.getLayerFields();
    const embalseLayer = DERA_HYDRAULIC_LAYERS[DERAHydraulicLayerType.EMBALSE];
    const presaLayer = DERA_HYDRAULIC_LAYERS[DERAHydraulicLayerType.PRESA];
    // sub-WEMB + sub-DERA-HENUM: Embalse y Presa carecen de cod_mun en CDAV.
    const isLayerWithoutCodMun =
      this.currentLayer === embalseLayer || this.currentLayer === presaLayer;

    // FILTRO PRINCIPAL: código municipal INE (omitido en capas sin cod_mun)
    if (options.codMun && !isLayerWithoutCodMun) {
      filters.push(`cod_mun='${this.escapeCQL(options.codMun)}'`);
    }

    // Filtro por nombre (opcional, para búsqueda específica)
    if (options.name) {
      filters.push(`${fields.nameField} ILIKE '%${this.escapeCQL(options.name)}%'`);
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Construye parámetros WFS para DERA
   */
  protected buildWFSParams(options: DERAHydraulicSearchOptions): Record<string, string> {
    const params: Record<string, string> = {
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      typeName: this.config.layerName,
      outputFormat: 'application/json',
      srsName: this.config.outputSRS,
      count: String(options.maxResults || 100),  // WFS 2.0 canónico, migrado en sub-WMVC 14-may
    };

    const cqlFilter = this.buildCQLFilter(options);
    if (cqlFilter) {
      params['CQL_FILTER'] = cqlFilter;
    }

    return params;
  }

  /**
   * Selecciona capa según tipo de infraestructura
   */
  private selectLayer(layerType: DERAHydraulicLayerType): void {
    this.currentLayer = DERA_HYDRAULIC_LAYERS[layerType];
    this.config.layerName = this.currentLayer;
  }

  /**
   * Detecta tipo de infraestructura por nombre
   *
   * Orden de matching (ver `.ptel/DECISION_DERA_G03_CATALOGACION_20260512.md`):
   *  1. manantial / fuente / nacimiento → MANANTIAL
   *  2. pozo / sondeo / captación → CAPTACION
   *  3. **presa / dique → PRESA** (sub-DERA-HENUM; ANTES de embalse/pantano
   *     porque la presa es UN componente del embalse, más específico).
   *  4. embalse / pantano → EMBALSE
   *  5. fallback → DEPOSITO
   *
   * Cambio sub-DERA-HENUM respecto a sub-WEMB: en WEMB el matching `presa →
   * EMBALSE` agrupaba dique y cuerpo de agua en la misma capa. Tras catalogar
   * `cav_02_g03_12_Presa` como capa propia, el detector separa ambas entidades
   * según la complementariedad funcional confirmada por el smoke (campo
   * `tipo='Dique de cierre de balsa'` en features Presa). El matching fuzzy
   * posterior reordena por similitud si el ground-truth difiere.
   */
  detectLayerType(name: string): DERAHydraulicLayerType {
    const lower = name.toLowerCase();

    if (lower.includes('manantial') || lower.includes('fuente') || lower.includes('nacimiento')) {
      return DERAHydraulicLayerType.MANANTIAL;
    }
    if (lower.includes('pozo') || lower.includes('sondeo') || lower.includes('captación') || lower.includes('captacion')) {
      return DERAHydraulicLayerType.CAPTACION;
    }
    // sub-DERA-HENUM: presa / dique → PRESA. Caso «Presa del embalse de X» gana
    // PRESA por aparecer antes en el orden, lo cual es correcto: la consulta
    // referencia explícitamente el dique como entidad principal.
    if (lower.includes('presa') || lower.includes('dique')) {
      return DERAHydraulicLayerType.PRESA;
    }
    // sub-WEMB: embalse / pantano → EMBALSE.
    if (lower.includes('embalse') || lower.includes('pantano')) {
      return DERAHydraulicLayerType.EMBALSE;
    }
    // Default: depósito
    return DERAHydraulicLayerType.DEPOSITO;
  }

  /**
   * Obtiene TODAS las infraestructuras de un municipio por código INE
   * 
   * Consulta las 3 capas (manantiales, depósitos, captaciones) y
   * devuelve todas las features encontradas.
   * 
   * @param codMun - Código INE del municipio (5 dígitos)
   * @returns Array de features de todas las capas
   * 
   * @example
   * const features = await geocoder.getByMunicipality('18051');
   * // Colomera: 3 depósitos + 9 manantiales = 12 infraestructuras
   */
  public async getByMunicipality(codMun: string): Promise<DERAHydraulicFeature[]> {
    const allFeatures: DERAHydraulicFeature[] = [];
    const layers = Object.values(DERA_HYDRAULIC_LAYERS);
    const embalseLayer = DERA_HYDRAULIC_LAYERS[DERAHydraulicLayerType.EMBALSE];
    const presaLayer = DERA_HYDRAULIC_LAYERS[DERAHydraulicLayerType.PRESA];

    for (const layer of layers) {
      // RESIL-WFS-S1: CAPTACION eliminada en CDAV — layer vacío indica no disponible
      if (!layer) continue;
      // sub-WEMB + sub-DERA-HENUM: Embalse y Presa no exponen cod_mun — filtrar por
      // código INE devuelve HTTP 400. El consumo va por
      // geocodeWithAutoLayer({name, layerType: EMBALSE | PRESA}).
      if (layer === embalseLayer || layer === presaLayer) continue;
      try {
        this.currentLayer = layer;
        this.config.layerName = layer;

        const params = this.buildWFSParams({ 
          name: '', 
          codMun,
          maxResults: 200,
        });

        const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
        const features = this.parseResponse(response.data) as DERAHydraulicFeature[];

        // Marcar capa origen
        features.forEach(f => {
          f.properties.sourceLayer = layer;
        });

        allFeatures.push(...features);
      } catch (error) {
        console.warn(`DERAHydraulic: Error en capa ${layer} para ${codMun}:`, error);
      }
    }

    // Restaurar capa por defecto
    this.currentLayer = DERA_HYDRAULIC_LAYERS[DERAHydraulicLayerType.DEPOSITO];
    this.config.layerName = this.currentLayer;

    return allFeatures;
  }

  /**
   * Geocodifica una infraestructura con detección automática de capa
   * 
   * Estrategia:
   * 1. Detecta tipo por nombre → selecciona capa
   * 2. Busca en esa capa primero
   * 3. Si no encuentra (score < threshold), prueba otras capas
   * 
   * @param options - Opciones de búsqueda (nombre + codMun recomendado)
   * @returns Resultado de geocodificación o null
   */
  public async geocodeWithAutoLayer(options: DERAHydraulicSearchOptions): Promise<GeocodingResult | null> {
    // Detectar tipo y seleccionar capa
    const detectedType = options.layerType || this.detectLayerType(options.name);
    this.selectLayer(detectedType);

    // Intentar con capa detectada
    let result = await this.geocodeLegacy(options);
    if (result && result.confidence >= 70) {
      result.sourceLayer = `DERA:${this.currentLayer}`;
      return result;
    }

    // Probar otras capas si no encuentra
    const allTypes = Object.values(DERAHydraulicLayerType);
    for (const type of allTypes) {
      if (type === detectedType) continue;

      this.selectLayer(type);
      result = await this.geocodeLegacy(options);

      if (result && result.confidence >= 70) {
        result.sourceLayer = `DERA:${this.currentLayer}`;
        return result;
      }
    }

    // Restaurar capa por defecto
    this.selectLayer(DERAHydraulicLayerType.DEPOSITO);
    return result;
  }

  /**
   * Obtiene estadísticas del geocodificador
   */
  public override getStats() {
    return {
      ...super.getStats(),
      currentLayer: this.currentLayer,
      availableLayers: Object.values(DERA_HYDRAULIC_LAYERS),
    };
  }
}

export default DERAHydraulicGeocoder;
